/*******************************************************************************
 * Copyright     : Copyright (c)  2020 Honeywell Inc. All rights reserved.
 *                 This work contains trade secrets and confidential materials
 *                 of Honeywell Inc., and its use or disclosure in whole or in
 *                 part without express written permission of Honeywell Inc.
 *                 is prohibited.
 *
 * Name          : APMSettingsSlice.ts
 *
 * Project Title : APM Settings Tool
 *
 * Description   : Redux Slice for APM Settings state
 *
 *
 * Company       : Honeywell Technology Solutions , Hyderabad
 *
 * @Author       : H422337
 *
 * @Date         : Feb 03,2022
 *
 * @Version      : Version 1.0
 *
 * By            :
 * On            :
 * Description   :
 *
 *
 ******************************************************************************/

import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { ISavedNewAPMFileDetails } from "models/aircraftDetails";
import {
  APMSettingField,
  IAPMSettingsState,
  IGeneratedFileStatus,
} from "models/apmSettings";
import { AppThunk, RootState } from "stores/GlobalStore";
import { setSelectedApmRecordData } from "stores/ApmWorkFlowStore";
import { setNextDetailsData } from "stores/AircraftDetailsStore";
import { apiCallBegan, apiVersion, apmURLs } from "api";
import { ISelectApmFilesList } from "models/selectApmFile";

export const initialState: IAPMSettingsState = {
  savedNewAPMFileDetails: null,
  saveAPMSettingsLoading: false,
  saveFileNameModalOpen: false,
  updatedAPMSettingsDetails: null,
  generateFileNameLoading: false,
  generateFileNameSaveStatus: false,
  checkFileNameLoading: false,
  isFileNameExist: null,
  GeneratedFileName: null,
};

const APMSettingsSlice = createSlice({
  name: "apmSettings",
  initialState,
  reducers: {
    beginSaveAPMDetailsLoader(apmSettingsState: IAPMSettingsState) {
      apmSettingsState.saveAPMSettingsLoading = true;
    },
    setSavedAPMDetailsAndGenerate(
      apmSettingsState: IAPMSettingsState,
      action: PayloadAction<ISavedNewAPMFileDetails>
    ) {
      apmSettingsState.saveAPMSettingsLoading = false;
      apmSettingsState.savedNewAPMFileDetails = action.payload;
      apmSettingsState.saveFileNameModalOpen = true;
    },
    setSavedAPMDetails(
      apmSettingsState: IAPMSettingsState,
      action: PayloadAction<ISavedNewAPMFileDetails>
    ) {
      apmSettingsState.saveAPMSettingsLoading = false;
      apmSettingsState.savedNewAPMFileDetails = action.payload;
    },

    SaveAPMDetailsLoaderError(apmSettingsState: IAPMSettingsState) {
      apmSettingsState.saveAPMSettingsLoading = false;
    },
    setUpdatedAPMSettingsDetails(
      apmSettingsState: IAPMSettingsState,
      action: PayloadAction<APMSettingField[]>
    ) {
      apmSettingsState.updatedAPMSettingsDetails = action.payload;
    },
    setSaveFilePopUpModal(
      apmSettingsState: IAPMSettingsState,
      action: PayloadAction<boolean>
    ) {
      apmSettingsState.saveFileNameModalOpen = action.payload;
    },
    beginGenerateFileNameLoader(apmSettingsState: IAPMSettingsState) {
      apmSettingsState.generateFileNameLoading = true;
    },
    setGenerateFileNameSaveStatus(
      apmSettingsState: IAPMSettingsState,
      action: PayloadAction<IGeneratedFileStatus>
    ) {
      if (action.payload?.status?.toLowerCase() === "success") {
        apmSettingsState.generateFileNameSaveStatus = true;
      }
      apmSettingsState.generateFileNameLoading = false;
      apmSettingsState.GeneratedFileName = action.payload;
    },

    beginCheckFileNameLoader(apmSettingsState: IAPMSettingsState) {
      apmSettingsState.checkFileNameLoading = true;
    },
    setIsFileNameExistStatus(
      apmSettingsState: IAPMSettingsState,
      action: PayloadAction<ISelectApmFilesList>
    ) {
      apmSettingsState.checkFileNameLoading = false;
      if (action?.payload?.partNumbers?.length !== 0) {
        apmSettingsState.isFileNameExist = true;
      } else {
        apmSettingsState.isFileNameExist = false;
      }
    },
    CheckFileNameLoaderError(apmSettingsState: IAPMSettingsState) {
      apmSettingsState.checkFileNameLoading = false;
    },
    SetGenerateFileNameError(apmSettingsState: IAPMSettingsState) {
      apmSettingsState.generateFileNameSaveStatus = false;
      apmSettingsState.generateFileNameLoading = false;
    },
    resetAPMSettingsStore(apmSettingsState: IAPMSettingsState) {
      apmSettingsState.updatedAPMSettingsDetails =
        initialState.updatedAPMSettingsDetails;
      apmSettingsState.saveFileNameModalOpen =
        initialState.saveFileNameModalOpen;
      apmSettingsState.generateFileNameSaveStatus =
        initialState.generateFileNameSaveStatus;
      apmSettingsState.GeneratedFileName = initialState.GeneratedFileName;
      apmSettingsState.checkFileNameLoading = initialState.checkFileNameLoading;
      apmSettingsState.generateFileNameLoading =
        initialState.generateFileNameLoading;
      apmSettingsState.isFileNameExist = initialState.isFileNameExist;
      apmSettingsState.saveAPMSettingsLoading =
        initialState.saveAPMSettingsLoading;
      apmSettingsState.savedNewAPMFileDetails =
        initialState.savedNewAPMFileDetails;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(
        setSelectedApmRecordData,
        (apmSettingsState: IAPMSettingsState, action) => {
          apmSettingsState.updatedAPMSettingsDetails =
            action.payload.apmSettings;
        }
      )
      .addCase(
        setNextDetailsData,
        (apmSettingsState: IAPMSettingsState, action) => {
          apmSettingsState.updatedAPMSettingsDetails =
            action.payload.apmSettings;
        }
      );
  },
});

const {
  beginSaveAPMDetailsLoader,
  setSavedAPMDetailsAndGenerate,
  setSavedAPMDetails,
  SaveAPMDetailsLoaderError,
  beginGenerateFileNameLoader,
  setGenerateFileNameSaveStatus,
  beginCheckFileNameLoader,
  setIsFileNameExistStatus,
  SetGenerateFileNameError,
  CheckFileNameLoaderError,
} = APMSettingsSlice.actions;

export const {
  setUpdatedAPMSettingsDetails,
  setSaveFilePopUpModal,
  resetAPMSettingsStore,
} = APMSettingsSlice.actions;
export const apmSettingsReducer = APMSettingsSlice.reducer;

export const SaveAPMSettingsData =
  (
    APMSettingsFieldsData: APMSettingField[] | null,
    buttonType: string
  ): AppThunk =>
  async (dispatch, getState) => {
    const savedAPMFileDetails = getState().apmWorkFlow.currentApmFileRecord;

    const url = apiVersion.v1 + apmURLs.APM_SETTINGS;

    const data = {
      make: savedAPMFileDetails?.make,
      model: savedAPMFileDetails?.model,
      tailNumber: savedAPMFileDetails?.tailNumber,
      partNumber: savedAPMFileDetails?.partNumber,
      baseFileName: savedAPMFileDetails?.baseFileName,
      serialNumber: savedAPMFileDetails?.serialNumber,
      customerName: savedAPMFileDetails?.customerName,
      customerEmail: savedAPMFileDetails?.customerEmail,
      customerPhone: savedAPMFileDetails?.customerPhoneNo,
      customerId: savedAPMFileDetails?.customerId,
      lsapFileName: savedAPMFileDetails?.lsapFileName,
      status: savedAPMFileDetails?.status,
      historyId: savedAPMFileDetails?.historyId,
      previousScreen: "4",
      apmSettings: APMSettingsFieldsData,
    };

    dispatch(
      apiCallBegan({
        url,
        data,
        method: "PUT",
        onStart: beginSaveAPMDetailsLoader.type,
        onSuccess:
          buttonType === "SaveAndGenerate"
            ? setSavedAPMDetailsAndGenerate.type
            : setSavedAPMDetails.type,
        onError: SaveAPMDetailsLoaderError.type,
        showGlobalError: true,
      })
    );
  };

export const CheckExistingFileName =
  (fileName: string): AppThunk =>
  async (dispatch, getState) => {
    const customerId = getState().customer.selectedCustomer?.customerId;
    const url = apiVersion.v1 + apmURLs.APM_SETTINGS_FILE_LIST_URL;
    const params = { customerId: customerId, fileName: fileName };

    dispatch(
      apiCallBegan({
        url,
        params,
        onStart: beginCheckFileNameLoader.type,
        onSuccess: setIsFileNameExistStatus.type,
        onError: CheckFileNameLoaderError.type,
        showGlobalError: true,
      })
    );
  };

export const SaveFileName =
  (fileName: string): AppThunk =>
  async (dispatch, getState) => {
    const savedAPMFileDetails = getState().apmWorkFlow.currentApmFileRecord;
    const historyId = savedAPMFileDetails?.historyId?.toString();
    const url = apiVersion.v1 + `/apm-settings/${historyId}/generate`;

    const data = {
      fileName: fileName,
      historyId: historyId,
    };

    dispatch(
      apiCallBegan({
        url,
        data,
        method: "POST",
        onStart: beginGenerateFileNameLoader.type,
        onSuccess: setGenerateFileNameSaveStatus.type,
        onError: SetGenerateFileNameError.type,
        showGlobalError: true,
      })
    );
  };

export const selectSaveFileModalOpen = (state: RootState): boolean =>
  state.apmSettings.saveFileNameModalOpen;

export const selectSaveDetailsLoading = (state: RootState): boolean =>
  state.apmSettings.saveAPMSettingsLoading;

export const selectUpdatedAPMSettingsData = (
  state: RootState
): APMSettingField[] | null => state.apmSettings.updatedAPMSettingsDetails;

export const selectGenerateFileNameLoading = (state: RootState): boolean =>
  state.apmSettings.generateFileNameLoading;

export const selectGenerateFileNameSaveStatus = (state: RootState): boolean =>
  state.apmSettings.generateFileNameSaveStatus;

export const selectCheckExistFileNameLoading = (state: RootState): boolean =>
  state.apmSettings.checkFileNameLoading;

export const selectExistingFileNameStatus = (
  state: RootState
): boolean | null => state.apmSettings.isFileNameExist;
